/**
 * EngineeringPDFGenerator.tsx - Generator raportów PDF dla modułów inżynierskich
 * PlexiSystem Engineering Module
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Funkcja konwersji polskich znaków
const convertPolishChars = (text: string): string => {
  return text
    .replace(/ą/g, 'a').replace(/Ą/g, 'A')
    .replace(/ć/g, 'c').replace(/Ć/g, 'C')
    .replace(/ę/g, 'e').replace(/Ę/g, 'E')
    .replace(/ł/g, 'l').replace(/Ł/g, 'L')
    .replace(/ń/g, 'n').replace(/Ń/g, 'N')
    .replace(/ó/g, 'o').replace(/Ó/g, 'O')
    .replace(/ś/g, 's').replace(/Ś/g, 'S')
    .replace(/ź/g, 'z').replace(/Ź/g, 'Z')
    .replace(/ż/g, 'z').replace(/Ż/g, 'Z');
};

export class EngineeringPDFGenerator {
  private pdf: any;
  private primaryColor = [249, 115, 22]; // orange-500
  private darkBg = [39, 39, 42]; // zinc-800
  private dangerColor = [239, 68, 68]; // red-500
  private warningColor = [245, 158, 11]; // amber-500
  private successColor = [34, 197, 94]; // green-500
  private infoColor = [59, 130, 246]; // blue-500

  constructor() {
    this.pdf = new jsPDF() as any;
  }

  /**
   * Główna metoda generująca raport dla dowolnego modułu
   */
  public generateModuleReport(
    moduleType: string,
    input: any,
    result: any
  ): void {
    switch (moduleType) {
      case 'plate':
        this.generatePlateReport(input, result);
        break;
      case 'fastener':
        this.generateFastenerReport(input, result);
        break;
      case 'compliance':
        this.generateComplianceReport(input, result);
        break;
      case 'packaging':
        this.generatePackagingReport(input, result);
        break;
      case 'thermal':
        this.generateThermalReport(input, result);
        break;
      case 'ergonomics':
        this.generateErgonomicsReport(input, result);
        break;
      default:
        throw new Error(`Nieznany typ modułu: ${moduleType}`);
    }
    
    this.savePDF(moduleType);
  }

  /**
   * Raport zgodności z normami (kontynuacja)
   */
  private generateComplianceReport(input: any, result: any): void {
    this.drawHeader('RAPORT ZGODNOŚCI Z NORMAMI', 'Moduł weryfikacji PlexiSystem');
    
    let y = 55;
    
    // Status zgodności
    const compliantColor = result.compliant ? this.successColor : this.dangerColor;
    this.pdf.setFillColor(...compliantColor);
    this.pdf.rect(15, y, this.pdf.internal.pageSize.getWidth() - 30, 20, 'F');
    
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(16);
    this.pdf.text(
      result.compliant ? 'ZGODNE Z NORMAMI' : 'NIEZGODNE - WYMAGA KOREKT',
      this.pdf.internal.pageSize.getWidth() / 2,
      y + 13,
      { align: 'center' }
    );
    
    y += 30;
    
    // Parametry
    this.drawSection(y, 'PARAMETRY WYROBU', () => {
      const tableData = [
        ['Zastosowanie', this.translateUseCase(input.use_case)],
        ['Materiał', input.material],
        ['Grubość', `${input.thickness_mm} mm`],
        ['Promień krawędzi', `${input.edges_radius_mm} mm`],
        ['Stabilizacja UV', input.uv_stabilized ? 'Tak' : 'Nie'],
        ['Kontakt z żywnością', input.food_contact ? 'Tak' : 'Nie']
      ];
      
      this.pdf.autoTable({
        startY: y + 8,
        body: tableData,
        theme: 'striped',
        bodyStyles: this.getTableBodyStyle(),
        margin: { left: 15, right: 15 }
      });
      
      return this.pdf.lastAutoTable.finalY;
    });
    
    y = this.pdf.lastAutoTable.finalY + 15;
    
    // Wymagania i status
    if (result.requirements_met && result.requirements_met.length > 0) {
      this.drawSection(y, 'WERYFIKACJA WYMAGAŃ', () => {
        const reqData = result.requirements_met.map((req: any) => [
          req.requirement,
          req.details,
          req.status === 'pass' ? '✓' : req.status === 'warning' ? '!' : '✗'
        ]);
        
        this.pdf.autoTable({
          startY: y + 8,
          head: [['Wymaganie', 'Stan faktyczny', 'Status']],
          body: reqData,
          theme: 'grid',
          headStyles: this.getTableHeaderStyle(),
          bodyStyles: this.getTableBodyStyle(),
          didDrawCell: (data: any) => {
            // Kolorowanie statusu
            if (data.column.index === 2 && data.row.section === 'body') {
              const status = data.cell.raw;
              if (status === '✓') {
                this.pdf.setTextColor(...this.successColor);
              } else if (status === '!') {
                this.pdf.setTextColor(...this.warningColor);
              } else if (status === '✗') {
                this.pdf.setTextColor(...this.dangerColor);
              }
            }
          },
          margin: { left: 15, right: 15 }
        });
        
        return this.pdf.lastAutoTable.finalY;
      });
      
      y = this.pdf.lastAutoTable.finalY + 15;
    }
    
    // Normy i certyfikaty
    if (result.required_certificates && result.required_certificates.length > 0) {
      this.drawWarningBox(y, 'WYMAGANE CERTYFIKATY', result.required_certificates, this.infoColor);
      y += 40;
    }
    
    // Zalecenia
    if (result.recommendations && result.recommendations.length > 0) {
      this.drawWarningBox(y, 'ZALECENIA', result.recommendations, this.warningColor);
    }
    
    // Druga strona - Deklaracja zgodności
    if (result.declaration_text) {
      this.pdf.addPage();
      this.drawHeader('DEKLARACJA ZGODNOŚCI', '');
      
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor(0, 0, 0);
      
      const lines = this.pdf.splitTextToSize(convertPolishChars(result.declaration_text), this.pdf.internal.pageSize.getWidth() - 40);
      this.pdf.text(lines, 20, 60);
    }
    
    this.drawFooter(1);
  }

  /**
   * Raport pakowania
   */
  private generatePackagingReport(input: any, result: any): void {
    this.drawHeader('PROJEKT OPAKOWANIA TRANSPORTOWEGO', 'Moduł logistyczny PlexiSystem');
    
    let y = 55;
    
    // Parametry produktu
    this.drawSection(y, 'PARAMETRY PRODUKTU', () => {
      const tableData = [
        ['Wymiary [mm]', input.product_dims_mm.join(' × ')],
        ['Masa [kg]', input.product_mass_kg.toString()],
        ['Trasa', this.translateRoute(input.route)],
        ['Delikatność', this.translateFragility(input.fragility)]
      ];
      
      this.pdf.autoTable({
        startY: y + 8,
        body: tableData,
        theme: 'striped',
        bodyStyles: this.getTableBodyStyle(),
        margin: { left: 15, right: 15 }
      });
      
      return this.pdf.lastAutoTable.finalY;
    });
    
    y = this.pdf.lastAutoTable.finalY + 15;
    
    // Specyfikacja kartonu
    this.drawSection(y, 'SPECYFIKACJA OPAKOWANIA', () => {
      // Karty z parametrami
      this.drawResultCards([
        { 
          label: 'Wymiary zewn.', 
          value: result.carton.external_mm.map((d: number) => `${d}`).join('×'), 
          unit: 'mm', 
          color: this.primaryColor 
        },
        { 
          label: 'Klasa tektury', 
          value: result.carton.material_grade, 
          unit: '', 
          color: this.infoColor 
        },
        { 
          label: 'BCT', 
          value: result.carton.BCT_required_N.toString(), 
          unit: 'N', 
          color: this.dangerColor 
        },
        { 
          label: 'ECT', 
          value: result.carton.ECT_required_kN_m.toFixed(1), 
          unit: 'kN/m', 
          color: this.warningColor 
        }
      ], y + 10);
      
      return y + 40;
    });
    
    y += 50;
    
    // Zabezpieczenia
    this.drawSection(y, 'ZABEZPIECZENIA', () => {
      const protectionData = [
        ['Typ pianki', result.protection.foam_type],
        ['Gęstość pianki', `${result.protection.foam_density_kg_m3} kg/m³`],
        ['Grubość pianki', `${result.protection.foam_thickness_mm} mm`],
        ['Narożniki', result.protection.corner_protectors ? 'Tak' : 'Nie'],
        ['Folia bąbelkowa', result.protection.bubble_wrap ? 'Tak' : 'Nie']
      ];
      
      this.pdf.autoTable({
        startY: y + 8,
        body: protectionData,
        theme: 'striped',
        bodyStyles: this.getTableBodyStyle(),
        margin: { left: 15, right: 15 }
      });
      
      return this.pdf.lastAutoTable.finalY;
    });
    
    y = this.pdf.lastAutoTable.finalY + 15;
    
    // Paletyzacja
    this.drawSection(y, 'PALETYZACJA', () => {
      this.pdf.setFillColor(239, 246, 255);
      this.pdf.roundedRect(15, y + 8, this.pdf.internal.pageSize.getWidth() - 30, 35, 3, 3, 'F');
      
      this.pdf.setTextColor(59, 130, 246);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(12);
      this.pdf.text(convertPolishChars(result.palletization.pallet_type), 25, y + 18);
      
      this.pdf.setTextColor(0, 0, 0);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      this.pdf.text(convertPolishChars(`Kartonów na palecie: ${result.palletization.cartons_per_pallet}`), 25, y + 26);
      this.pdf.text(convertPolishChars(`Liczba warstw: ${result.palletization.layers}`), 25, y + 32);
      this.pdf.text(convertPolishChars(`Efektywność: ${result.palletization.efficiency_percent}%`), 25, y + 38);
      this.pdf.text(convertPolishChars(`Układ: ${result.palletization.arrangement}`), 120, y + 26);
      
      return y + 45;
    });
    
    y += 55;
    
    // Koszty
    this.drawSection(y, 'SZACUNKOWE KOSZTY', () => {
      const costData = Object.entries(result.cost_estimate.breakdown).map(([key, value]: [string, any]) => [
        this.translateCostItem(key),
        `${value.toFixed(2)} PLN`
      ]);
      costData.push(['RAZEM', `${result.cost_estimate.per_unit.toFixed(2)} PLN`]);
      
      this.pdf.autoTable({
        startY: y + 8,
        body: costData,
        theme: 'striped',
        bodyStyles: this.getTableBodyStyle(),
        margin: { left: 15, right: 15 }
      });
      
      return this.pdf.lastAutoTable.finalY;
    });
    
    // Oznaczenia transportowe
    if (result.shipping_marks && result.shipping_marks.length > 0) {
      this.pdf.addPage();
      this.drawHeader('OZNACZENIA TRANSPORTOWE', '');
      
      let markY = 60;
      result.shipping_marks.forEach((mark: string) => {
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(14);
        this.pdf.text(convertPolishChars(mark), this.pdf.internal.pageSize.getWidth() / 2, markY, { align: 'center' });
        markY += 10;
      });
    }
    
    this.drawFooter(1);
  }

  /**
   * Raport procesów termicznych
   */
  private generateThermalReport(input: any, result: any): void {
    this.drawHeader(
      input.process === 'bend' ? 'PROCES GIĘCIA TERMICZNEGO' : 'PROCES TERMOFORMOWANIA',
      'Moduł technologiczny PlexiSystem'
    );
    
    let y = 55;
    
    // Parametry procesu
    this.drawSection(y, 'PARAMETRY PROCESU', () => {
      const tableData = [
        ['Materiał', input.material],
        ['Grubość', `${input.thickness_mm} mm`],
        ['Proces', input.process === 'bend' ? 'Gięcie' : 'Termoformowanie'],
        ['Temperatura zalecana', `${result.temperatures.recommended_C[0]}-${result.temperatures.recommended_C[1]}°C`],
        ['Temperatura wybrana', `${result.temperatures.selected_C}°C`]
      ];
      
      if (input.process === 'bend' && input.dimensions?.bend_radius_mm) {
        tableData.push(['Promień gięcia', `${input.dimensions.bend_radius_mm} mm`]);
      }
      
      this.pdf.autoTable({
        startY: y + 8,
        body: tableData,
        theme: 'striped',
        bodyStyles: this.getTableBodyStyle(),
        margin: { left: 15, right: 15 }
      });
      
      return this.pdf.lastAutoTable.finalY;
    });
    
    y = this.pdf.lastAutoTable.finalY + 15;
    
    // Czasy procesu
    this.drawSection(y, 'HARMONOGRAM CZASOWY', () => {
      this.drawResultCards([
        { label: 'Nagrzewanie', value: result.timing.heating_min.toString(), unit: 'min', color: this.dangerColor },
        { label: 'Chłodzenie', value: result.timing.cooling_min.toString(), unit: 'min', color: this.infoColor },
        { label: 'Czas całkowity', value: result.timing.total_min.toString(), unit: 'min', color: this.successColor }
      ], y + 10);
      
      return y + 40;
    });
    
    y += 50;
    
    // Rozszerzalność termiczna
    if (result.expansion && result.expansion.deltaL_mm) {
      this.drawSection(y, 'ROZSZERZALNOŚĆ TERMICZNA', () => {
        this.pdf.setFillColor(254, 242, 242);
        this.pdf.roundedRect(15, y + 8, this.pdf.internal.pageSize.getWidth() - 30, 25, 3, 3, 'F');
        
        this.pdf.setTextColor(239, 68, 68);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(11);
        this.pdf.text(convertPolishChars('UWAGA - Kompensacja rozszerzalności'), 25, y + 18);
        
        this.pdf.setTextColor(0, 0, 0);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(10);
        this.pdf.text(convertPolishChars(`ΔL = ${result.expansion.deltaL_mm.toFixed(2)} mm`), 25, y + 26);
        if (result.expansion.deltaW_mm) {
          this.pdf.text(convertPolishChars(`ΔW = ${result.expansion.deltaW_mm.toFixed(2)} mm`), 100, y + 26);
        }
        
        return y + 35;
      });
      
      y += 45;
    }
    
    // Harmonogram szczegółowy
    if (result.process_schedule && result.process_schedule.length > 0) {
      this.drawSection(y, 'SZCZEGÓŁOWY HARMONOGRAM PROCESU', () => {
        let scheduleY = y + 10;
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(9);
        
        result.process_schedule.slice(0, 15).forEach((step: string) => {
          this.pdf.text(convertPolishChars(step), 20, scheduleY);
          scheduleY += 5;
        });
        
        return scheduleY;
      });
      
      y = this.pdf.lastAutoTable ? this.pdf.lastAutoTable.finalY + 10 : y + 80;
    }
    
    // Wskazówki
    if (result.tips && result.tips.length > 0) {
      this.drawWarningBox(y, 'WSKAZÓWKI PROCESOWE', result.tips, this.infoColor);
      y += 40;
    }
    
    // Ostrzeżenia
    if (result.warnings && result.warnings.length > 0) {
      this.drawWarningBox(y, 'OSTRZEŻENIA', result.warnings, this.dangerColor);
    }
    
    this.drawFooter(1);
  }

  /**
   * Raport ergonomii
   */
  private generateErgonomicsReport(input: any, result: any): void {
    this.drawHeader('OCENA ERGONOMII EKSPOZYTORA', 'Moduł projektowy PlexiSystem');
    
    let y = 55;
    
    // Oceny
    this.drawSection(y, 'WYNIKI OCENY', () => {
      this.drawResultCards([
        { label: 'Ocena ogólna', value: result.overall_score.toString(), unit: '/100', color: this.primaryColor },
        { label: 'Golden zone', value: result.golden_zone_usage.percentage.toFixed(0), unit: '%', color: this.warningColor },
        { label: 'Dostępność', value: result.accessibility_score.toString(), unit: '/100', color: this.successColor }
      ], y + 10);
      
      return y + 40;
    });
    
    y += 50;
    
    // Analiza widoczności
    if (result.visibility_analysis && result.visibility_analysis.length > 0) {
      this.drawSection(y, 'ANALIZA WIDOCZNOŚCI PÓŁEK', () => {
        const visData = result.visibility_analysis.map((shelf: any) => [
          `Półka ${shelf.shelf_number}`,
          `${shelf.height_cm} cm`,
          shelf.zone,
          this.translateVisibility(shelf.visibility)
        ]);
        
        this.pdf.autoTable({
          startY: y + 8,
          head: [['Półka', 'Wysokość', 'Strefa', 'Widoczność']],
          body: visData,
          theme: 'grid',
          headStyles: this.getTableHeaderStyle(),
          bodyStyles: this.getTableBodyStyle(),
          margin: { left: 15, right: 15 }
        });
        
        return this.pdf.lastAutoTable.finalY;
      });
      
      y = this.pdf.lastAutoTable.finalY + 15;
    }
    
    // Rekomendacje
    if (result.recommendations) {
      if (result.recommendations.general && result.recommendations.general.length > 0) {
        this.drawWarningBox(y, 'REKOMENDACJE OGÓLNE', result.recommendations.general, this.infoColor);
        y += 40;
      }
      
      if (result.recommendations.shelf_adjustments && result.recommendations.shelf_adjustments.length > 0) {
        this.drawSection(y, 'ZALECANE KOREKTY PÓŁEK', () => {
          const adjustData = result.recommendations.shelf_adjustments.map((adj: any) => [
            `Półka ${adj.shelf}`,
            `${adj.current_height} cm`,
            `${adj.recommended_height} cm`,
            adj.reason
          ]);
          
          this.pdf.autoTable({
            startY: y + 8,
            head: [['Półka', 'Obecna wys.', 'Zalecana wys.', 'Powód']],
            body: adjustData,
            theme: 'grid',
            headStyles: this.getTableHeaderStyle(),
            bodyStyles: this.getTableBodyStyle(),
            margin: { left: 15, right: 15 }
          });
          
          return this.pdf.lastAutoTable.finalY;
        });
      }
    }
    
    this.drawFooter(1);
  }

  // Metody pomocnicze

  private drawHeader(title: string, subtitle: string): void {
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    
    // Gradient background
    this.drawGradientBackground(0, 45);
    
    // Logo placeholder
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.circle(20, 22, 8, 'F');
    this.pdf.setTextColor(...this.primaryColor);
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('ENG', 20, 25, { align: 'center' });
    
    // Title
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(20);
    this.pdf.text(convertPolishChars(title), pageWidth / 2, 20, { align: 'center' });
    
    if (subtitle) {
      this.pdf.setFontSize(12);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(convertPolishChars(subtitle), pageWidth / 2, 28, { align: 'center' });
    }
    
    // Date and document number
    this.pdf.setFontSize(10);
    this.pdf.text(`Data: ${new Date().toLocaleDateString('pl-PL')}`, pageWidth - 20, 15, { align: 'right' });
    this.pdf.text(`Dokument: #${Date.now().toString().slice(-8)}`, pageWidth - 20, 20, { align: 'right' });
  }

  private drawGradientBackground(y: number, height: number): void {
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const steps = 20;
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps;
      const color = [
        249 - ratio * 20,
        115 - ratio * 10,
        22 + ratio * 10
      ];
      this.pdf.setFillColor(color[0], color[1], color[2]);
      this.pdf.rect(0, y + (height / steps) * i, pageWidth, height / steps, 'F');
    }
  }

  private drawSection(y: number, title: string, content: () => number): void {
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(39, 39, 42);
    this.pdf.text(convertPolishChars(title), 15, y);
    
    return content();
  }

  private drawResultCards(cards: Array<{ label: string; value: string; unit: string; color: number[] }>, y: number): void {
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const cardWidth = (pageWidth - 45) / 4;
    const cardHeight = 25;
    
    cards.forEach((card, index) => {
      const x = 15 + index * (cardWidth + 3);
      
      // Card background
      this.pdf.setFillColor(250, 250, 251);
      this.pdf.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');
      
      // Color accent
      this.pdf.setFillColor(...card.color);
      this.pdf.rect(x, y, cardWidth, 3, 'F');
      
      // Text
      this.pdf.setTextColor(100, 100, 100);
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(convertPolishChars(card.label), x + cardWidth/2, y + 8, { align: 'center' });
      
      this.pdf.setTextColor(...card.color);
      this.pdf.setFontSize(16);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(card.value, x + cardWidth/2, y + 16, { align: 'center' });
      
      this.pdf.setTextColor(100, 100, 100);
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(card.unit, x + cardWidth/2, y + 22, { align: 'center' });
    });
  }

  private drawWarningBox(y: number, title: string, items: string[], color: number[]): void {
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const boxHeight = 10 + items.length * 6;
    
    // Background
    this.pdf.setFillColor(color[0], color[1], color[2]);
    this.pdf.setOpacity(0.1);
    this.pdf.roundedRect(15, y, pageWidth - 30, boxHeight, 3, 3, 'F');
    this.pdf.setOpacity(1);
    
    // Border
    this.pdf.setDrawColor(...color);
    this.pdf.setLineWidth(0.5);
    this.pdf.roundedRect(15, y, pageWidth - 30, boxHeight, 3, 3, 'S');
    
    // Title
    this.pdf.setTextColor(...color);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.text(convertPolishChars(title), 20, y + 7);
    
    // Items
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    let itemY = y + 14;
    items.forEach(item => {
      const lines = this.pdf.splitTextToSize(convertPolishChars(`• ${item}`), pageWidth - 50);
      lines.forEach((line: string) => {
        this.pdf.text(line, 25, itemY);
        itemY += 5;
      });
    });
  }

  private drawFooter(pageNum: number): void {
    const pageHeight = this.pdf.internal.pageSize.getHeight();
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
    
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(128, 128, 128);
    this.pdf.setFont('helvetica', 'normal');
    
    this.pdf.text('PlexiSystem Engineering Module', 15, pageHeight - 12);
    this.pdf.text('(c) 2025 PlexiSystem', 15, pageHeight - 8);
    
    const now = new Date();
    this.pdf.text(
      `Wygenerowano: ${now.toLocaleDateString('pl-PL')} ${now.toLocaleTimeString('pl-PL')}`,
      pageWidth / 2,
      pageHeight - 12,
      { align: 'center' }
    );
    
    this.pdf.text(
      `Strona ${pageNum}`,
      pageWidth - 15,
      pageHeight - 12,
      { align: 'right' }
    );
    
    this.pdf.setFontSize(8);
    this.pdf.text('www.plexisystem.pl', pageWidth - 15, pageHeight - 8, { align: 'right' });
  }

  private getTableHeaderStyle(): any {
    return {
      fillColor: this.primaryColor,
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    };
  }

  private getTableBodyStyle(): any {
    return {
      fontSize: 10,
      cellPadding: 3
    };
  }

  // Funkcje tłumaczące
  private translateBoundary(boundary: string): string {
    const translations: Record<string, string> = {
      'simply_supported': 'Swobodnie podparte',
      'clamped': 'Utwierdzone',
      'mixed': 'Mieszane'
    };
    return translations[boundary] || boundary;
  }

  private translateJointType(type: string): string {
    const translations: Record<string, string> = {
      'fixed': 'Stałe',
      'hinge': 'Zawiasowe',
      'removable': 'Rozłączne',
      'sliding': 'Przesuwne'
    };
    return translations[type] || type;
  }

  private translateUseCase(useCase: string): string {
    const translations: Record<string, string> = {
      'machine_guard': 'Osłony maszyn',
      'food_contact': 'Kontakt z żywnością',
      'pos_display': 'Ekspozytory POS',
      'uv_heat_resistance': 'Odporność UV/temperatura',
      'medical': 'Zastosowania medyczne',
      'cleanroom': 'Pomieszczenia czyste',
      'explosion_proof': 'Strefy przeciwwybuchowe'
    };
    return translations[useCase] || useCase;
  }

  private translateRoute(route: string): string {
    const translations: Record<string, string> = {
      'local': 'Transport lokalny',
      'domestic': 'Transport krajowy',
      'intl': 'Transport międzynarodowy',
      'air': 'Transport lotniczy'
    };
    return translations[route] || route;
  }

  private translateFragility(fragility: string): string {
    const translations: Record<string, string> = {
      'low': 'Niska',
      'medium': 'Średnia',
      'high': 'Wysoka'
    };
    return translations[fragility] || fragility;
  }

  private translateCostItem(item: string): string {
    const translations: Record<string, string> = {
      'carton': 'Karton',
      'foam': 'Pianka',
      'pallet': 'Paleta',
      'corners': 'Narożniki'
    };
    return translations[item] || item;
  }

  private translateVisibility(visibility: string): string {
    const translations: Record<string, string> = {
      'excellent': 'Doskonała',
      'good': 'Dobra',
      'fair': 'Średnia',
      'poor': 'Słaba'
    };
    return translations[visibility] || visibility;
  }

  private savePDF(moduleType: string): void {
    const filename = `${moduleType}_${new Date().toISOString().slice(0, 10)}_${Date.now().toString().slice(-6)}.pdf`;
    this.pdf.save(filename);
  }
}
